import * as React from 'react';
import { z } from 'zod';
import { ArrowRight, ReceiptText, RefreshCw } from 'lucide-react';
import { Alert } from './Alert';
import { Badge } from './Badge';
import { Button } from './Button';
import { Card, CardBody, CardHeader } from './Card';
import { Switch } from './Checkbox';
import { EmptyState } from './EmptyState';
import { FormActions, Label } from './Field';
import { Form, FormField, type ZodForm } from './Form';
import { Icon } from './Icon';
import { Grid, Inline, Show, Stack } from './Layout';
import { ListRow } from './List';
import { Select } from './Select';
import { Loading } from './Spinner';
import { Text } from './Text';

export interface QuickBooksListOption {
  value: string;
  label: string;
}

/** The value a charge row carries while it falls through to the default. */
export const QUICKBOOKS_USE_DEFAULT = '__unmapped__';

export interface QuickBooksChargeMapping {
  /** The Relay charge — the label as it appears on invoices. */
  label: string;
  /** The QuickBooks item it routes to, or QUICKBOOKS_USE_DEFAULT. */
  itemId: string;
}

const FORM_ID = 'quickbooks-mapping-form';

/**
 * Every charge has to land somewhere, so the default item is the one thing
 * this cannot be saved without. Whether the chosen ids still exist over in
 * QuickBooks is a live lookup, which stays with the container.
 */
export const quickBooksMappingSchema = z.object({
  defaultItemId: z.string().min(1, 'Pick a default item'),
  cashMethodId: z.string(),
  cardMethodId: z.string(),
  autoPush: z.boolean(),
});

export type QuickBooksMappingValues = z.infer<typeof quickBooksMappingSchema>;

export interface QuickBooksMappingViewProps {
  /** QuickBooks products and services. */
  itemOptions: QuickBooksListOption[];
  /** QuickBooks payment methods. */
  methodOptions: QuickBooksListOption[];
  /** From `useZodForm(quickBooksMappingSchema, …)`. */
  form: ZodForm<typeof quickBooksMappingSchema>;
  /** The company's charges and where each one goes. Empty shows the placeholder. */
  charges: QuickBooksChargeMapping[];
  onChargeItemChange: (label: string, itemId: string) => void;
  saving?: boolean;
  onSave: (values: QuickBooksMappingValues) => void;
}

const MAPPING_HEADER = (
  <CardHeader
    title="Mapping"
    description="Where each charge and payment type lands in QuickBooks."
  />
);

export interface QuickBooksMappingStateCardProps {
  /** Reading the lists, or failed to. */
  state: 'loading' | 'error';
  /** What went wrong, when it did. */
  errorMessage?: React.ReactNode;
  onRetry?: () => void;
}

/**
 * The mapping card before it has anything to show: a spinner while the
 * QuickBooks lists load, or a warning with Try again when one of them
 * could not be read.
 */
export function QuickBooksMappingStateCard({
  state,
  errorMessage,
  onRetry,
}: QuickBooksMappingStateCardProps) {
  return (
    <Card>
      {MAPPING_HEADER}
      <CardBody>
        {state === 'error' ? (
          <Alert
            tone="warning"
            title="Could not read your QuickBooks lists"
            actions={
              <Button
                variant="outline"
                size="sm"
                leadingIcon={<RefreshCw />}
                onClick={onRetry}
              >
                Try again
              </Button>
            }
          >
            {errorMessage || 'QuickBooks did not respond.'}
          </Alert>
        ) : (
          <Loading label="Loading your QuickBooks lists" />
        )}
      </CardBody>
    </Card>
  );
}

const chargeFieldId = (label: string) =>
  `qb-charge-${label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;

/**
 * The QuickBooks setup screen: where each Relay charge lands, which payment
 * methods book cash and card payments, whether invoices sync on their own,
 * and Save. The container hydrates the values from the saved settings and
 * resolves the ids back into references when it saves.
 *
 *   <QuickBooksMappingView
 *     itemOptions={items} methodOptions={methods}
 *     form={form} charges={charges} onChargeItemChange={route}
 *     onSave={save}
 *   />
 */
export function QuickBooksMappingView({
  itemOptions,
  methodOptions,
  form,
  charges,
  onChargeItemChange,
  saving = false,
  onSave,
}: QuickBooksMappingViewProps) {
  const routedCount = charges.filter(
    (charge) => charge.itemId && charge.itemId !== QUICKBOOKS_USE_DEFAULT
  ).length;
  const rowOptions = [
    { value: QUICKBOOKS_USE_DEFAULT, label: 'Use default' },
    ...itemOptions,
  ];

  return (
    <Form form={form} gap={6} id={FORM_ID} onSubmit={onSave}>
      <Card padding="md">
        <CardHeader
          title="Where charges land"
          description="Each charge on a Relay invoice becomes a line on the QuickBooks invoice. Pick the QuickBooks product or service each one should use."
        />
        <Stack gap={0} divided>
          <CardBody>
            <Stack gap={6}>
              <FormField
                form={form}
                name="defaultItemId"
                label="Default item"
                description="Anything without a mapping of its own is billed here."
              >
                {(field) => (
                  <Select
                    options={itemOptions}
                    value={field.value || undefined}
                    onValueChange={field.onChange}
                    placeholder="Choose a QuickBooks item…"
                  />
                )}
              </FormField>

              {charges.length > 0 ? (
                <Inline gap={3} align="center" justify="between" wrap>
                  <Text variant="overline" tone="muted">
                    Your charges
                  </Text>
                  <Badge
                    tone={
                      routedCount === charges.length ? 'success' : 'neutral'
                    }
                    size="sm"
                  >
                    {routedCount} of {charges.length} routed
                  </Badge>
                </Inline>
              ) : (
                <EmptyState
                  icon={ReceiptText}
                  size="sm"
                  title="No charges to route yet"
                  description="Your common charges appear here once you have sent a few invoices."
                  footnote="Until then, every line uses the default above."
                />
              )}
            </Stack>
          </CardBody>

          {charges.length > 0 && (
            <Stack gap={0} divided>
              {/* Column headings, which is where the direction of the mapping
                  is stated. Off on a phone, where the rows stack. */}
              <Show above="sm" display="block" as="div">
                <ListRow inset="md" aria-hidden="true">
                  <Grid cols={2} gap={4} align="center" className="w-full">
                    <Text variant="overline" tone="muted">
                      Relay charge
                    </Text>
                    <Inline gap={2} align="center">
                      <Icon icon={ArrowRight} size="xs" tone="muted" />
                      <Text variant="overline" tone="muted">
                        QuickBooks item
                      </Text>
                    </Inline>
                  </Grid>
                </ListRow>
              </Show>
              {charges.map((charge) => (
                <ListRow key={charge.label} inset="md">
                  <Grid
                    cols={{ base: 1, sm: 2 }}
                    gap={4}
                    align="center"
                    className="w-full"
                  >
                    <Label htmlFor={chargeFieldId(charge.label)}>
                      {charge.label}
                    </Label>
                    <Select
                      id={chargeFieldId(charge.label)}
                      options={rowOptions}
                      value={charge.itemId || QUICKBOOKS_USE_DEFAULT}
                      onValueChange={(value) =>
                        onChargeItemChange(charge.label, value)
                      }
                    />
                  </Grid>
                </ListRow>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>

      <Card>
        <CardHeader
          title="Payment methods"
          description="QuickBooks records a payment method on every payment. Match yours so payments book correctly."
        />
        <CardBody>
          <Grid cols={{ base: 1, md: 2 }} gap={4}>
            <FormField
              form={form}
              name="cashMethodId"
              label="Cash payments"
              description="Used when you mark an invoice as paid yourself."
            >
              {(field) => (
                <Select
                  options={methodOptions}
                  value={field.value || undefined}
                  onValueChange={field.onChange}
                  placeholder="Choose a QuickBooks payment method…"
                />
              )}
            </FormField>
            <FormField
              form={form}
              name="cardMethodId"
              label="Card payments"
              description="Used for anything taken through Square or RoadSync."
            >
              {(field) => (
                <Select
                  options={methodOptions}
                  value={field.value || undefined}
                  onValueChange={field.onChange}
                  placeholder="Choose a QuickBooks payment method…"
                />
              )}
            </FormField>
          </Grid>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Sync"
          description="How invoices get from Relay into your books."
        />
        <CardBody>
          <FormField form={form} name="autoPush">
            {(field) => (
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                label="Send invoices automatically"
                description="Send each invoice to QuickBooks when you send it, record the payment when it is paid, and take that payment back off if you reverse it. Turn this off to send invoices one at a time instead."
              />
            )}
          </FormField>
        </CardBody>
      </Card>

      {/* No rule above these: the cards already close themselves off. */}
      <FormActions align="between">
        <Text variant="body-sm" tone="muted">
          Saved settings apply to invoices sent from now on.
        </Text>
        <Button variant="primary" form={FORM_ID} type="submit" loading={saving}>
          {saving ? 'Saving…' : 'Save mapping'}
        </Button>
      </FormActions>
    </Form>
  );
}
